"use client";

import { cn } from "@repo/ui";

import { Container } from "@/app/(marketing)/_components/ui/container";

import {
  MACOS_DOWNLOADS,
  MIN_MACOS_VERSION,
  MIN_WINDOWS_VERSION,
  WINDOWS_DOWNLOAD,
} from "./download-links";
import { AppleLogo, WindowsLogo } from "./os-icons";
import { scrollToSetup, useDetectedOS } from "./use-detected-os";

export default function PlatformDownloads() {
  const os = useDetectedOS();

  return (
    <section id="downloads" className="scroll-mt-24 py-16 lg:py-20">
      <Container>
        <div className="mb-10 flex flex-col items-start gap-4">
          <div className="border-border rounded-full border px-3 py-1">
            <p className="text-muted-foreground font-mono text-sm tracking-[-0.28px]">
              Downloads
            </p>
          </div>
          <h2 className="text-foreground text-heading-32 md:text-heading-40 max-w-[600px] text-balance">
            Choose your platform
          </h2>
          <p className="text-muted-foreground max-w-[520px] text-lg">
            Signed and notarized builds. The app keeps itself up to date, so you
            only download it once.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <PlatformTile
            icon={<AppleLogo className="size-5" />}
            title="macOS"
            subtitle={`${MIN_MACOS_VERSION} or later`}
            highlighted={os === "mac"}
            links={[
              {
                href: MACOS_DOWNLOADS.appleSilicon.href,
                label: "Apple Silicon",
                note: MACOS_DOWNLOADS.appleSilicon.note,
                primary: true,
              },
              {
                href: MACOS_DOWNLOADS.intel.href,
                label: "Intel",
                note: MACOS_DOWNLOADS.intel.note,
              },
            ]}
            footnote="Not sure which Mac you have? Apple menu, then About This Mac. A chip named Apple M1, M2 or newer is Apple Silicon."
          />

          <PlatformTile
            icon={<WindowsLogo className="size-5" />}
            title="Windows"
            subtitle={`${MIN_WINDOWS_VERSION} or later (64-bit)`}
            highlighted={os === "windows"}
            links={[
              {
                href: WINDOWS_DOWNLOAD.href,
                label: "Download installer",
                note: WINDOWS_DOWNLOAD.note,
                primary: true,
              },
            ]}
          />
        </div>
      </Container>
    </section>
  );
}

interface DownloadLink {
  href: string;
  label: string;
  note?: string;
  primary?: boolean;
}

function PlatformTile({
  icon,
  title,
  subtitle,
  highlighted,
  links,
  footnote,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  highlighted: boolean;
  links: DownloadLink[];
  footnote?: string;
}) {
  return (
    <div
      className={cn(
        "bg-card flex flex-col gap-6 rounded-2xl border p-6 sm:p-8",
        highlighted ? "border-marketing-blue" : "border-border",
      )}
    >
      <div className="flex items-center gap-3">
        <div className="border-border text-foreground flex size-11 items-center justify-center rounded-xl border">
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-foreground text-heading-20 font-medium text-balance">
            {title}
          </h3>
          <p className="text-muted-foreground text-sm">{subtitle}</p>
        </div>
        {highlighted ? (
          <span className="border-marketing-blue/30 text-marketing-blue rounded-full border px-3 py-1 text-xs font-medium">
            Your device
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        {links.map((link) => {
          // Only the visitor's own platform gets the filled, primary-color button.
          // Other platforms stay neutral/outline so we don't push a download that
          // isn't for this machine (per review feedback).
          const filled = Boolean(link.primary && highlighted);
          return (
            <a
              key={link.label}
              href={link.href}
              onClick={scrollToSetup}
              className={cn(
                "flex items-center justify-between gap-3 rounded-full px-5 py-3 text-sm font-medium transition-colors",
                filled
                  ? "bg-marketing-blue text-white hover:opacity-90"
                  : "border-border text-foreground hover:bg-muted border",
              )}
            >
              <span>{link.label}</span>
              {link.note ? (
                <span
                  className={cn(
                    "text-xs font-normal",
                    filled ? "text-white/70" : "text-muted-foreground",
                  )}
                >
                  {link.note}
                </span>
              ) : null}
            </a>
          );
        })}
      </div>

      {footnote ? (
        <p className="text-muted-foreground text-xs">{footnote}</p>
      ) : null}
    </div>
  );
}
